import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import logoGold from '@/assets/logo-gold-white.png';
import logoClean from '@/assets/royal-logo-clean.png';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const links = [
    { to: '/', label: t('home') },
    { to: '/about', label: t('about') },
    { to: '/apartments', label: t('apartments') },
    { to: '/destinations', label: t('destinations') },
    { to: '/reviews', label: t('reviews') },
    { to: '/booking', label: t('booking') },
    { to: '/contact', label: t('contact') },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Full-screen mobile menu — rendered as sibling, above everything */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[100] bg-background">
          {/* Subtle decorative accents */}
          <div className="absolute top-20 right-0 w-64 h-64 rounded-full bg-accent/8 blur-[100px]" />
          <div className="absolute bottom-32 left-0 w-72 h-72 rounded-full bg-golden/10 blur-[120px]" />

          {/* Top bar with logo and close */}
          <div className="section-padding flex items-center justify-between h-20">
            <Link to="/" className="relative z-10">
              <img src={logoClean} alt="Sivota Royal Luxury Retreat" className="h-14 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher scrolled={true} />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-foreground">
                <X size={28} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Menu content */}
          <div className="relative flex flex-col items-center justify-center px-8" style={{ height: 'calc(100% - 5rem)' }}>
            {links.map((l, i) => (
              <Link
                key={l.to}
                to={l.to}
                className="group flex items-center gap-4 py-3"
                style={{ animation: `menuFadeIn 0.4s ease-out ${i * 60}ms both` }}
              >
                <span className={`font-serif text-3xl sm:text-4xl font-light transition-colors duration-300 ${
                  location.pathname === l.to
                    ? 'text-accent'
                    : 'text-foreground/80 group-hover:text-foreground'
                }`}>
                  {l.label}
                </span>
                <ArrowRight
                  size={18}
                  className="text-foreground/0 group-hover:text-accent group-hover:translate-x-1 transition-all duration-300"
                />
              </Link>
            ))}

            <div className="w-16 h-px bg-gradient-to-r from-transparent via-golden/50 to-transparent my-5" />

            <Link
              to="/booking"
              className="mt-1 bg-accent text-accent-foreground px-8 py-3 text-xs tracking-[0.2em] uppercase hover:bg-accent/90 transition-colors"
            >
              {t('checkAvailability')}
            </Link>
          </div>
        </div>
      )}

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-background/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <nav aria-label="Main navigation" className="section-padding flex items-center justify-between h-20 lg:h-24">
          <Link to="/">
            <img
              src={scrolled ? logoClean : logoGold}
              alt="Sivota Royal Luxury Retreat"
              className="h-14 lg:h-16 w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {links.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-xs tracking-[0.12em] uppercase transition-colors duration-300 hover:opacity-70 ${
                  scrolled ? 'text-foreground' : 'text-white'
                } ${location.pathname === l.to ? 'opacity-70' : ''}`}
              >
                {l.label}
              </Link>
            ))}
            <LanguageSwitcher scrolled={scrolled} />
          </div>

          <div className="flex items-center gap-4 lg:hidden">
            <LanguageSwitcher scrolled={scrolled} />
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className={`transition-colors ${scrolled ? 'text-foreground' : 'text-white'}`}
            >
              <Menu size={28} aria-hidden="true" />
            </button>
          </div>
        </nav>
      </header>
    </>
  );
}
